import { useMemo, useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Ban, Pencil, RotateCcw } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { RowActionsMenu } from '@/components/ui/row-actions-menu';
import { useConsentMutations } from '@/features/patient-record/hooks/useConsentMutations';
import { consentStatusToBadgeVariant } from '@/lib/clinicalBadges';
import { formatDate } from '@/lib/format';
import type { ConsentViewModel } from '@/features/patient-record/types';

import { ConfirmDeleteDialog } from '../_shared/ConfirmDeleteDialog';

import { ConsentAttachmentButton } from './ConsentAttachmentButton';
import { ConsentForm } from './ConsentForm';

interface ConsentListProps {
  readonly guid: string;
  readonly consents: readonly ConsentViewModel[];
}

/**
 * Sorted view: live consents first (add order, preserving BE ordering);
 * withdrawn consents muted at the bottom, per PRD § "As a user, I can see
 * the patient's consents".
 */
function partition(rows: readonly ConsentViewModel[]): {
  readonly live: readonly ConsentViewModel[];
  readonly withdrawn: readonly ConsentViewModel[];
} {
  const live: ConsentViewModel[] = [];
  const withdrawn: ConsentViewModel[] = [];
  for (const row of rows) (row.status === 'WITHDRAWN' ? withdrawn : live).push(row);
  return { live, withdrawn };
}

export function ConsentList({ guid, consents }: ConsentListProps): ReactElement {
  const { t } = useTranslation();
  const [editingId, setEditingId] = useState<string | null>(null);
  const [pendingWithdraw, setPendingWithdraw] = useState<ConsentViewModel | null>(null);
  const { withdraw, reinstate } = useConsentMutations(guid);

  const { live, withdrawn } = useMemo(() => partition(consents), [consents]);
  const editingConsent =
    editingId !== null ? (consents.find((c) => c.id === editingId) ?? null) : null;

  if (consents.length === 0) {
    return (
      <p className="text-sm text-muted-foreground italic">
        {t('patient.consent.empty')}
      </p>
    );
  }

  return (
    <>
      <ul className="divide-y divide-border/50 rounded-md border border-border/60">
        {[...live, ...withdrawn].map((consent) => (
          <li key={consent.id} className="px-3 py-2">
            <ConsentRow
              guid={guid}
              consent={consent}
              onEdit={() => {
                setEditingId(consent.id);
              }}
              onWithdraw={() => {
                setPendingWithdraw(consent);
              }}
              onReinstate={() => {
                reinstate.mutate(consent.id);
              }}
            />
          </li>
        ))}
      </ul>
      {editingConsent !== null && (
        <ConsentForm
          guid={guid}
          consent={editingConsent}
          onClose={() => {
            setEditingId(null);
          }}
        />
      )}
      {pendingWithdraw !== null && (
        <ConfirmDeleteDialog
          title={t('patient.consent.withdrawConfirmTitle', {
            name: pendingWithdraw.consentType,
          })}
          message={t('patient.consent.withdrawConfirmMessage')}
          confirmLabel={t('patient.consent.withdraw')}
          busy={withdraw.isPending}
          onCancel={() => {
            setPendingWithdraw(null);
          }}
          onConfirm={() => {
            withdraw.mutate(pendingWithdraw.id, {
              onSettled: () => {
                setPendingWithdraw(null);
              },
            });
          }}
        />
      )}
    </>
  );
}

interface ConsentRowProps {
  readonly guid: string;
  readonly consent: ConsentViewModel;
  readonly onEdit: () => void;
  readonly onWithdraw: () => void;
  readonly onReinstate: () => void;
}

function ConsentRow({
  guid,
  consent,
  onEdit,
  onWithdraw,
  onReinstate,
}: ConsentRowProps): ReactElement {
  const { t } = useTranslation();
  const isWithdrawn = consent.status === 'WITHDRAWN';
  const canWithdraw = consent.status === 'SIGNED';

  return (
    <div className="group">
      {/* Row 1: type + status badge + row actions all share one flex line so
          the h-5 Badge and h-7 action buttons vertically center as siblings
          instead of drifting apart across two nested containers. */}
      <div className="flex items-center gap-2">
        <span
          className={`text-sm font-medium flex-1 min-w-0 truncate ${
            isWithdrawn
              ? 'text-muted-foreground line-through'
              : 'text-card-foreground'
          }`}
        >
          {consent.consentType}
        </span>
        <Badge
          variant={consentStatusToBadgeVariant(consent.status)}
          className="text-xs h-5 shrink-0"
        >
          {t(`patient.consent.status.${consent.status}`)}
        </Badge>
        {/* Attach affordance lives with the row actions — only SIGNED
            consents can have supporting documents (PRD § "Attach supporting
            document"). Withdrawn rows keep the linked doc visible but
            read-only so audit context is preserved. */}
        {(consent.status === 'SIGNED' || consent.documentRef !== null) && (
          <ConsentAttachmentButton guid={guid} consent={consent} />
        )}
        <RowActionsMenu
          label={t('patient.consent.actionsLabel', { defaultValue: 'Consent actions' })}
          actions={[
            {
              label: t('patient.consent.edit'),
              icon: <Pencil className="h-3.5 w-3.5" aria-hidden="true" />,
              onSelect: onEdit,
            },
            canWithdraw && {
              label: t('patient.consent.withdraw'),
              icon: <Ban className="h-3.5 w-3.5" aria-hidden="true" />,
              onSelect: onWithdraw,
              destructive: true,
              separatorBefore: true,
            },
            isWithdrawn && {
              label: t('patient.consent.reinstate'),
              icon: <RotateCcw className="h-3.5 w-3.5" aria-hidden="true" />,
              onSelect: onReinstate,
              separatorBefore: true,
            },
          ]}
        />
      </div>
      {/* Row 2: metadata (signedAt / signedBy / notes). Rendered only when
          at least one is present so single-line consents don't gain empty
          vertical space. */}
      {(consent.signedAt !== null ||
        consent.signedBy !== null ||
        consent.notes !== null) && (
        <div className="mt-0.5 flex flex-wrap gap-x-3 gap-y-0.5 text-xs text-muted-foreground">
          {consent.signedAt !== null && (
            <span>
              {t('patient.consent.signedAt', { value: formatDate(consent.signedAt) })}
            </span>
          )}
          {consent.signedBy !== null && (
            <span>{t('patient.consent.signedBy', { value: consent.signedBy })}</span>
          )}
          {consent.notes !== null && (
            <span className="text-card-foreground/80 italic">{consent.notes}</span>
          )}
        </div>
      )}
    </div>
  );
}
